export type MenuAction =
  | 'DOWNLOAD-RECORDING'
  | 'SHARE-RECORDING'
  | 'UNSHARE-RECORDING'
  | 'DELETE-RECORDING'
  | 'PUBLISH-RECORDING'
  | 'UNPUBLISH-RECORDING'
  | 'COPY-RECORDING-LINK'
  | 'COPY-TO-ORGANIZATIONS'
  | 'SAMPLE-CONFIGURE'
  | 'SEND-FEEDBACK'
  | 'KEYBOARD-SHORTCUTS';

export type DriveNavMenuItem = {
  label: string;
  action: MenuAction;
};

type BuildDriveNavMenuItemsArgs = {
  canCopyRecording: boolean;
  currentOrgId?: number | null;
  driveOrgId?: number | null;
  isShared: boolean;
  appUserId?: number | null;
  uploadedById?: number | null;
  isAdmin: boolean;
  isOusterSuperAdmin: boolean;
  isPublished: boolean;
  driveOrigin?: string | null;
};

type DriveSensorInfoSummary = {
  sdkProductType?: string | null;
  sdkProductResolution?: string | number | null;
  sdkFwVersionCore?: string | null;
  sdkProductHwRevision?: string | null;
  sdkLidarMode?: string | null;
};

type DriveTitleSource = {
  recordingTitle?: string | null;
  uploadedBy?: string | null;
  sensorInfos?: DriveSensorInfoSummary[] | null;
};

export type DriveVizTitleProps = {
  text?: string;
  recordingTitle?: string;
  details?: string;
};

export function buildDriveNavMenuItems({
  canCopyRecording,
  currentOrgId,
  driveOrgId,
  isShared,
  appUserId,
  uploadedById,
  isAdmin,
  isOusterSuperAdmin,
  isPublished,
  driveOrigin,
}: BuildDriveNavMenuItemsArgs): DriveNavMenuItem[] {
  const items: DriveNavMenuItem[] = [
    {
      label: 'Download Recording',
      action: 'DOWNLOAD-RECORDING',
    },
    {
      label: 'Copy the visualization link',
      action: 'COPY-RECORDING-LINK',
    },
  ];

  if (canCopyRecording) {
    items.push({
      label: 'Copy to Organization(s)',
      action: 'COPY-TO-ORGANIZATIONS',
    });
  }

  const isOwnerOrg = driveOrgId === currentOrgId;

  if (isOwnerOrg) {
    if (currentOrgId !== 1) {
      items.push({
        label: isShared
          ? 'Stop Sharing with Ouster'
          : 'Share Recording with Ouster',
        action: isShared ? 'UNSHARE-RECORDING' : 'SHARE-RECORDING',
      });
    }

    if (appUserId === uploadedById || isAdmin) {
      items.push({
        label: 'Delete Recording',
        action: 'DELETE-RECORDING',
      });
    }

    if (isOusterSuperAdmin) {
      items.push({
        label: 'Configure Sample Access',
        action: 'SAMPLE-CONFIGURE',
      });
    }
  }

  if (isOwnerOrg && isAdmin) {
    if (!isPublished && driveOrigin !== 'SAVE') {
      items.push({
        label: 'Publish Recording',
        action: 'PUBLISH-RECORDING',
      });
    }

    if (isPublished) {
      items.push({
        label: 'Manage Published Recording',
        action: 'UNPUBLISH-RECORDING',
      });
    }
  }

  items.push(
    {
      label: 'Send Feedback',
      action: 'SEND-FEEDBACK',
    },
    {
      label: 'Keyboard Shortcuts',
      action: 'KEYBOARD-SHORTCUTS',
    }
  );

  return items;
}

export function buildDriveVizTitleProps(
  driveInfo?: DriveTitleSource | null,
  isShared = false
): DriveVizTitleProps {
  if (!driveInfo) {
    return {};
  }

  const sensorInfos = driveInfo.sensorInfos ?? [];
  const sensorInfo = sensorInfos[0];
  const isMultisensor = sensorInfos.length > 1;
  const details: string[] = [];

  if (isMultisensor) {
    details.push('Multi-sensor');
  } else if (sensorInfo) {
    details.push(
      `${sensorInfo.sdkProductType} - ${sensorInfo.sdkProductResolution} | ${sensorInfo.sdkFwVersionCore} | Rev ${sensorInfo.sdkProductHwRevision} | ${sensorInfo.sdkLidarMode}`
    );
  }

  if (driveInfo.uploadedBy) {
    details.push(`by ${driveInfo.uploadedBy}`);
  }

  if (isShared) {
    details.push('Shared with Ouster');
  }

  const recTitle = driveInfo.recordingTitle || undefined;
  const detailsText = details.filter(Boolean).join(' | ');

  if (!recTitle) {
    return {
      text: detailsText,
    };
  }

  return {
    recordingTitle: recTitle,
    details: detailsText,
  };
}

export function buildDriveVizLink(
  origin: string,
  publishedDriveUuid?: string,
  organizationId?: number,
  driveId?: number | string
): string {
  if (publishedDriveUuid) {
    return `${origin}/share/${publishedDriveUuid}`;
  }

  return `${origin}/organization/${organizationId}/drive/${driveId}`;
}
